import { BACKEND_URL } from "../../config";
import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import { setKindredPoints } from "../auth/authSlice";

export const authApi = createApi({
  reducerPath: "authApi",
  keepUnusedDataFor: 0,
  refetchOnMountOrArgChange: true,
  baseQuery: fetchBaseQuery({
    baseUrl: `${BACKEND_URL}`,
    prepareHeaders: (headers, { getState }) => {
      const token = getState().auth.token;
      if (token) {
        // include token in req header
        headers.set("authorization", `Bearer ${token}`);
      }
      return headers;
    },
    fetchFn: async (input, init) => {
      const response = await fetch(input, init);
      if (response.status === 204) {
        return new Response(JSON.stringify([]), { status: 200 }); // Transform 204 to an empty array
      }
      return response;
    },
  }),
  endpoints: (builder) => ({
    getUserInfo: builder.query({
      query: () => `/user/portal/get-user-info`,
    }),
    getRoleDetails: builder.query({
      query: (positionsObj) => {
        const positionsArray = positionsObj?.positions || [];

        const positionsParam =
          positionsArray.length > 1
            ? positionsArray.join(",")
            : positionsArray[0];
        
          return `/user/portal/role-details?positions=${positionsParam}`;
      },
    }),
    getAllTechnicians: builder.query({
      query: (location) => {
        return `/user/portal/get-active-employee${location ? `/${location}` : ''}`;
      },
    }),
    getEmployeesByPosition: builder.query({
      query: (positions) => {
        const posParam = Array.isArray(positions)
          ? positions.map(p => encodeURIComponent(p)).join(",")
          : encodeURIComponent(positions);
        return `/user/portal/get-employees-by-position?positions=${posParam}`;
      },
    }),
    getActiveTechs: builder.query({
      query: ({ page = 1, limit = 50, location = "Houston" } = {}) => {
        const params = new URLSearchParams();
        if (page) params.append('page', page);
        if (limit) params.append('limit', limit);
        if (location) params.append('location', location);
        const queryString = params.toString();
        return `/sales/portal/get-active-techs-light${queryString ? `?${queryString}` : ''}`;
      },
      providesTags: ['ActiveTechs'],
    }),
    getKindredPoints: builder.query({
      query: () => `/user/portal/get-kindred-points`,
      async onQueryStarted(arg, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;
          dispatch(setKindredPoints(data));
        } catch (err) {
          // Handle error if needed
        }
      },
    }),
    // Actions -> Directory. No argument = the active-employee list, which is
    // what every existing caller gets (ChangeEmployeePositionDialog passes
    // undefined too, so the two share one cache entry).
    // { scope: "all" } asks the BFF for every past and present employee; the
    // BFF serves that cohort only to the President position and answers
    // `canViewAllPersonnel: false` with the active list to anyone else, so the
    // pop-out renders its Active/All toggle from the response rather than
    // deciding for itself. (Levi 2026-10-06, thread 1a112552e1177e2c.)
    getDirectory: builder.query({
      query: ({ scope } = {}) =>
        `/user/portal/get-directory${scope ? `?scope=${encodeURIComponent(scope)}` : ''}`,
    }),
    getActiveTechsNew: builder.query({
      query: ({ keyword = '', location = '' } = {}) => {
        const params = new URLSearchParams();
        if (keyword) params.append('keyword', keyword);
        if (location) params.append('location', location);
        const queryString = params.toString();
        return `/search/portal/get/active-techs-new${queryString ? `?${queryString}` : ''}`;
      },
      keepUnusedDataFor: 0, // Ensure fresh data on each fetch
    }),
    updatePassword: builder.mutation({
      query: ({ currentPass, pass, passConfirm }) => ({
        url: `/user/portal/update-password`,
        method: "POST",
        body: { currentPass, pass, passConfirm },
      }),
    }),
  }),
});

export const { useGetRoleDetailsQuery, useGetAllTechniciansQuery, useGetActiveTechsQuery, useGetUserInfoQuery,
  useGetKindredPointsQuery, useGetEmployeesByPositionQuery, useGetActiveTechsNewQuery, useGetDirectoryQuery,
  useUpdatePasswordMutation } = authApi;
